/**
 * Page registry: declares the page set for each UI variant. `App.jsx` looks
 * up the active variant at runtime (via `useUIVariant`) and renders the
 * matching pages.
 *
 * To add a new variant:
 *   1. Drop the new page components under `src/pages/<variant>/`.
 *   2. Export the 7 page identifiers (Home, PlaySelection, Session,
 *      SessionComplete, Progress, RuleList, RulePlay).
 *   3. Add an entry in `PAGE_SETS` below.
 *   4. Add the catalogue entry in `src/config/ui.config.js`.
 *
 * Each variant is wrapped with React.lazy() so only the chosen variant's
 * code is fetched. Variant switches trigger a short Suspense fallback while
 * the new chunk loads.
 */

import { lazy } from 'react';

const classicPages = {
  HomePage: lazy(() => import('./HomePage.jsx')),
  PlaySelectionPage: lazy(() => import('./PlaySelectionPage.jsx')),
  SessionPage: lazy(() => import('./SessionPage.jsx')),
  SessionCompletePage: lazy(() => import('./SessionCompletePage.jsx')),
  ProgressPage: lazy(() => import('./ProgressPage.jsx')),
  RuleListPage: lazy(() => import('./RuleListPage.jsx')),
  RulePlayPage: lazy(() => import('./RulePlayPage.jsx')),
};

const cosmicPages = {
  HomePage: lazy(() => import('./cosmic/HomePage.jsx')),
  PlaySelectionPage: lazy(() => import('./cosmic/PlaySelectionPage.jsx')),
  SessionPage: lazy(() => import('./cosmic/SessionPage.jsx')),
  SessionCompletePage: lazy(() => import('./cosmic/SessionCompletePage.jsx')),
  ProgressPage: lazy(() => import('./cosmic/ProgressPage.jsx')),
  RuleListPage: lazy(() => import('./cosmic/RuleListPage.jsx')),
  RulePlayPage: lazy(() => import('./cosmic/RulePlayPage.jsx')),
};

const summerPages = {
  HomePage: lazy(() => import('./summer/HomePage.jsx')),
  PlaySelectionPage: lazy(() => import('./summer/PlaySelectionPage.jsx')),
  SessionPage: lazy(() => import('./summer/SessionPage.jsx')),
  SessionCompletePage: lazy(() => import('./summer/SessionCompletePage.jsx')),
  ProgressPage: lazy(() => import('./summer/ProgressPage.jsx')),
  RuleListPage: lazy(() => import('./summer/RuleListPage.jsx')),
  RulePlayPage: lazy(() => import('./summer/RulePlayPage.jsx')),
};

const dinoPages = {
  HomePage: lazy(() => import('./dino/HomePage.jsx')),
  PlaySelectionPage: lazy(() => import('./dino/PlaySelectionPage.jsx')),
  SessionPage: lazy(() => import('./dino/SessionPage.jsx')),
  SessionCompletePage: lazy(() => import('./dino/SessionCompletePage.jsx')),
  ProgressPage: lazy(() => import('./dino/ProgressPage.jsx')),
  RuleListPage: lazy(() => import('./dino/RuleListPage.jsx')),
  RulePlayPage: lazy(() => import('./dino/RulePlayPage.jsx')),
};

const minoanPages = {
  HomePage: lazy(() => import('./minoan/HomePage.jsx')),
  PlaySelectionPage: lazy(() => import('./minoan/PlaySelectionPage.jsx')),
  SessionPage: lazy(() => import('./minoan/SessionPage.jsx')),
  SessionCompletePage: lazy(() => import('./minoan/SessionCompletePage.jsx')),
  ProgressPage: lazy(() => import('./minoan/ProgressPage.jsx')),
  RuleListPage: lazy(() => import('./minoan/RuleListPage.jsx')),
  RulePlayPage: lazy(() => import('./minoan/RulePlayPage.jsx')),
};

const PAGE_SETS = {
  classic: classicPages,
  cosmic: cosmicPages,
  summer: summerPages,
  dino: dinoPages,
  minoan: minoanPages,
};

/**
 * Return the page set for a given variant, falling back to classic when the
 * variant is unknown.
 */
export function getPagesForVariant(variant) {
  return PAGE_SETS[variant] ?? PAGE_SETS.classic;
}
